/**
 * Utility functions for bookmarks
 *
 * These utilities work with both database format (BookmarkData) and UI format (FormattedBookmark)
 */
import { BookmarkData, FormattedBookmark, BookmarkFilters } from './bookmarkTypes';

/**
 * Type guard to check if a bookmark is in FormattedBookmark format
 */
function isFormattedBookmark(
  bookmark: BookmarkData | FormattedBookmark
): bookmark is FormattedBookmark {
  return 'topicId' in bookmark && 'userId' in bookmark && 'createdAt' in bookmark;
}

/**
 * Apply filters to a list of bookmarks (works with both BookmarkData and FormattedBookmark formats)
 *
 * @param bookmarks Array of bookmarks to filter
 * @param filters Filter criteria to apply
 * @returns Filtered array of bookmarks
 */
export function applyFilters<T extends BookmarkData | FormattedBookmark>(
  bookmarks: T[],
  filters: BookmarkFilters
): T[] {
  let filteredBookmarks = [...bookmarks];

  // Apply search query filter
  if (filters.searchQuery) {
    const normalizedQuery = filters.searchQuery.toLowerCase().trim();
    filteredBookmarks = filteredBookmarks.filter(bookmark => {
      // Get fields based on the bookmark type
      const title = isFormattedBookmark(bookmark) ? bookmark.title : bookmark.title;
      const description = isFormattedBookmark(bookmark)
        ? bookmark.description
        : bookmark.description;
      const url = bookmark.url.toLowerCase();
      const summary = isFormattedBookmark(bookmark) ? bookmark.summary : bookmark.summary;
      const note = isFormattedBookmark(bookmark) ? bookmark.note : bookmark.note;

      // Search in all text fields
      return (
        (title && title.toLowerCase().includes(normalizedQuery)) ||
        (description && description.toLowerCase().includes(normalizedQuery)) ||
        url.includes(normalizedQuery) ||
        (summary && summary.toLowerCase().includes(normalizedQuery)) ||
        (note && note.toLowerCase().includes(normalizedQuery))
      );
    });
  }

  // Apply user filter
  if (filters.userIds && filters.userIds.length > 0) {
    filteredBookmarks = filteredBookmarks.filter(bookmark => {
      const userId = isFormattedBookmark(bookmark) ? bookmark.userId : bookmark.user_id;
      return filters.userIds?.includes(userId);
    });
  }

  // Apply domain filter
  if (filters.domains && filters.domains.length > 0) {
    filteredBookmarks = filteredBookmarks.filter(bookmark => {
      try {
        const url = new URL(bookmark.url);
        const domain = url.hostname;
        return filters.domains?.some(d => domain.includes(d));
      } catch {
        return false;
      }
    });
  }

  // Apply date range filter
  if (filters.dateRange) {
    filteredBookmarks = filteredBookmarks.filter(bookmark => {
      const createdAt = isFormattedBookmark(bookmark)
        ? new Date(bookmark.createdAt)
        : new Date(bookmark.created_at);

      let isInRange = true;

      if (filters.dateRange?.from) {
        const fromDate = new Date(filters.dateRange.from);
        isInRange = isInRange && createdAt >= fromDate;
      }

      if (filters.dateRange?.to) {
        const toDate = new Date(filters.dateRange.to);
        isInRange = isInRange && createdAt <= toDate;
      }

      return isInRange;
    });
  }

  // Apply processing status filter
  if (filters.processingStatus) {
    filteredBookmarks = filteredBookmarks.filter(bookmark => {
      const status = isFormattedBookmark(bookmark)
        ? bookmark.metadata.processingStatus
        : bookmark.metadata?.processing_status;

      return status === filters.processingStatus;
    });
  }

  return filteredBookmarks;
}

/**
 * Sorts bookmarks by creation date in ascending order (oldest first)
 * Works with both BookmarkData and FormattedBookmark
 */
export function sortByCreationDateAsc<T extends BookmarkData | FormattedBookmark>(
  bookmarks: T[]
): T[] {
  return [...bookmarks].sort((a, b) => {
    const dateA = isFormattedBookmark(a) ? new Date(a.createdAt) : new Date(a.created_at);
    const dateB = isFormattedBookmark(b) ? new Date(b.createdAt) : new Date(b.created_at);
    return dateA.getTime() - dateB.getTime();
  });
}

/**
 * Sorts bookmarks by creation date in descending order (newest first)
 * Works with both BookmarkData and FormattedBookmark
 */
export function sortByCreationDateDesc<T extends BookmarkData | FormattedBookmark>(
  bookmarks: T[]
): T[] {
  return [...bookmarks].sort((a, b) => {
    const dateA = isFormattedBookmark(a) ? new Date(a.createdAt) : new Date(a.created_at);
    const dateB = isFormattedBookmark(b) ? new Date(b.createdAt) : new Date(b.created_at);
    return dateB.getTime() - dateA.getTime();
  });
}
